import { Aide } from '@/components/ui/aide';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { licensesAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Search,
  Eye,
  Key,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { StatutBadge } from '../components/ui/StatutBadge';
import { libelle, optionsLibelles } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDateOuTiret } from '../lib/formats';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';

const OrganizationsPage = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'OWN_TRUCKS',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [orgsData, licensesData] = await Promise.all([
        organizationsAPI.getEvery(),
        licensesAPI.getEvery().catch(() => []),
      ]);
      setOrganizations(orgsData || []);
      setLicenses(licensesData || []);
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de charger les organisations'));
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = loadAll;

  // Une licence par organisation: la licence active en priorité
  const licenseByOrg = useMemo(() => {
    const map = {};
    (licenses || []).forEach((l) => {
      if (l.status === 'ACTIVE') {
        map[l.organization_id] = l;
      } else if (!map[l.organization_id]) {
        map[l.organization_id] = l;
      }
    });
    return map;
  }, [licenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        await organizationsAPI.update(editingOrg.id, formData);
        toast.success('Organisation mise à jour');
      } else {
        await organizationsAPI.create(formData);
        toast.success('Organisation créée');
      }
      setIsDialogOpen(false);
      setEditingOrg(null);
      setFormData({
        name: '',
        type: 'OWN_TRUCKS',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
      });
      loadOrganizations();
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible d’enregistrer l’organisation'));
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      type: org.type,
      email: org.email || '',
      phone: org.phone || '',
      address: org.address || '',
      city: org.city || '',
      country: org.country || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ?')) return;
    
    try {
      await organizationsAPI.delete(id);
      toast.success('Organisation supprimée');
      loadOrganizations();
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de supprimer l’organisation'));
    }
  };

  const filteredOrgs = useMemo(() => {
    return organizations.filter((org) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        (org.name && org.name.toLowerCase().includes(q)) ||
        (org.email && org.email.toLowerCase().includes(q));
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && org.is_active) ||
        (statusFilter === 'inactive' && !org.is_active);
      const lic = licenseByOrg[org.id];
      const matchesPlan = planFilter === 'all' || (lic && lic.plan === planFilter);
      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [organizations, searchTerm, statusFilter, planFilter, licenseByOrg]);
  const pagination = usePagination(filteredOrgs, { reinitialiser: `${searchTerm}|${statusFilter}|${planFilter}` });

  // Une liste vidée par un filtre n'est pas une plateforme sans client.
  const hasOrgFilters =
    Boolean(searchTerm) || statusFilter !== 'all' || planFilter !== 'all';

  const stats = useMemo(() => {
    const total = organizations.length;
    const active = organizations.filter((o) => o.is_active).length;
    const suspended = total - active;
    const monthAgo = new Date(Date.now() - 30 * 86400 * 1000).toISOString();
    const new30 = organizations.filter((o) => o.created_at && o.created_at >= monthAgo).length;
    const withoutLicense = organizations.filter((org) => !licenseByOrg[org.id]).length;
    const expiring = organizations.filter((org) => {
      const lic = licenseByOrg[org.id];
      if (!lic?.expires_at) return false;
      const days = (new Date(lic.expires_at).getTime() - Date.now()) / 86400000;
      return days >= 0 && days <= 30;
    }).length;
    return { total, active, suspended, new30, withoutLicense, expiring };
  }, [organizations, licenseByOrg]);

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground/70 mb-4" />
          <h2 className="text-muted-foreground mb-2">
            Accès refusé
          </h2>
          <p className="text-muted-foreground">
            Vous n'avez pas les permissions pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Organisations"
        description="Les entreprises clientes de la plateforme: une ligne par organisation, avec sa licence."
        stats={[
          { label: 'Total', valeur: stats.total, onClick: () => setStatusFilter('all') },
          { label: 'En activité', valeur: stats.active, ton: 'positif', onClick: () => setStatusFilter('active') },
          {
            label: 'Suspendues',
            valeur: stats.suspended,
            ton: stats.suspended ? 'critique' : 'neutre',
            onClick: () => setStatusFilter('inactive'),
          },
          { label: 'Nouvelles (30 jours)', valeur: stats.new30 },
        ]}
        alertesSousTitre
        alertes={[
          stats.withoutLicense > 0 && {
            niveau: 'critique',
            nombre: stats.withoutLicense,
            court: 'sans licence',
            texte: `${stats.withoutLicense} ${stats.withoutLicense > 1 ? 'organisations sont sans licence' : 'organisation est sans licence'}. Créer ou réactiver une licence avant de former les utilisateurs.`,
            action: { label: 'Gérer les licences', to: '/licenses' },
          },
          stats.expiring > 0 && {
            niveau: 'attention',
            nombre: stats.expiring,
            court: stats.expiring > 1 ? 'licences à renouveler' : 'licence à renouveler',
            texte: `${stats.expiring} ${stats.expiring > 1 ? 'licences arrivent' : 'licence arrive'} à échéance dans les 30 prochains jours. Sécuriser le renouvellement.`,
            action: { label: 'Gérer les licences', to: '/licenses' },
          },
        ]}
        actions={
          <Button
            onClick={() => {
              setEditingOrg(null);
              setIsDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle organisation
          </Button>
        }
      />

      {/* La fenêtre est pilotée par l'état de la page: le bouton est en haut à droite. */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingOrg ? 'Modifier' : 'Créer'} une organisation
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-xl border border-border bg-muted/50 p-3">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-bold text-foreground">Fiche de l’organisation</p>
                  <Aide titre="Fiche de l’organisation">
                    Cette fiche décrit le client. Ce qu’il peut utiliser dépend de sa licence et des modules activés.
                  </Aide>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Activité</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-md"
                  required
                >
                  {optionsLibelles('orgType').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium mb-1">Adresse</label>
                <Input
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ville</label>
                <Input
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pays</label>
                <Input
                  value={formData.country}
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                />
              </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingOrg ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      <div className="bg-card rounded-xl shadow-sm border border-border">
        <div className="sticky top-0 z-20 rounded-t-xl bg-card/95 backdrop-blur p-4 border-b border-border flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-muted-foreground/70" />
            <Input
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="État"
            className="rounded-md border border-border px-3 py-2 text-sm"
          >
            <option value="all">Tous les états</option>
            <option value="active">En activité</option>
            <option value="inactive">Suspendues</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            aria-label="Formule"
            className="rounded-md border border-border px-3 py-2 text-sm"
          >
            <option value="all">Toutes les formules</option>
            {optionsLibelles('licensePlan').map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <LoadingState shape="table" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Activité</TableHead>
                <TableHead>Licence</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>État</TableHead>
                <TableHead>Créée le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagination.lignes.map((org) => {
                const lic = licenseByOrg[org.id];
                return (
                  <TableRow key={org.id} className="group hover:bg-muted/50">
                    <TableCell className="font-medium">
                      <button
                        onClick={() => navigate(`/organizations/${org.id}`)}
                        className="text-foreground hover:text-flame-ink font-bold text-left"
                      >
                        {org.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {libelle('orgType', org.type, 'À préciser')}
                      </span>
                    </TableCell>
                    <TableCell>
                      {lic ? (
                        <div className="flex items-center gap-1.5">
                          <Key className="w-3.5 h-3.5 text-process" />
                          <span className="text-xs font-bold text-process">{libelle('licensePlan', lic.plan)}</span>
                          <StatutBadge domaine="licenseStatus" code={lic.status} className="text-[10px] px-1.5 py-0.5" />
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/70">Aucune</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{org.email || '—'}</TableCell>
                    <TableCell className="text-sm">{org.city || '—'}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          org.is_active
                            ? 'bg-success/15 text-success'
                            : 'bg-destructive/15 text-destructive'
                        }`}
                      >
                        {org.is_active ? 'En activité' : 'Suspendue'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateOuTiret(org.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/organizations/${org.id}`)}
                          title="Voir le détail"
                        >
                          <Eye className="w-4 h-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(org)}
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(org.id)}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {!loading && filteredOrgs.length > 0 ? <BarrePagination pagination={pagination} nom="sociétés" /> : null}

        {!loading && filteredOrgs.length === 0 && (
          <EmptyState
            variant={hasOrgFilters ? 'filtered' : 'empty'}
            icon={Building2}
            title={
              hasOrgFilters
                ? 'Aucune organisation ne correspond'
                : 'Aucune organisation créée'
            }
            description={
              hasOrgFilters
                ? 'Vos organisations sont toujours là : la recherche ou les filtres de statut et de formule en masquent l’affichage.'
                : 'Une organisation est un client de la plateforme : son propre parc, ses propres utilisateurs, sa propre facturation. C’est le premier objet à créer, tout le reste s’y rattache.'
            }
            actionLabel={hasOrgFilters ? undefined : 'Créer une organisation'}
            onAction={() => {
              setEditingOrg(null);
              setIsDialogOpen(true);
            }}
            className="border-0"
          />
        )}
      </div>
    </div>
  );
};

export default OrganizationsPage;
